"use client";

import { useCallback, useId, useMemo, useRef, useState } from "react";
import type { Square } from "chess.js";
import {
  FILES,
  PIECE_LABEL,
  isLightSquare,
  squaresFor,
  useBoardInteraction,
  type Arrow,
  type Movable,
  type MoveIntent,
  type Orientation,
} from "./interaction";
import { PromotionPicker } from "./PromotionPicker";
import { PieceArt } from "./PieceArt";
import { useAppearance } from "@/components/site/AppearanceProvider";
import { BOARDS } from "@/lib/appearance";

export type BoardProps = {
  fen: string;
  orientation?: Orientation;
  movable?: Movable;
  lastMove?: { from: Square; to: Square } | null;
  arrows?: Arrow[];
  /** Extra square tints, e.g. for puzzle feedback. */
  tints?: Partial<Record<Square, string>>;
  onMove?: (m: MoveIntent) => boolean | void;
  className?: string;
  label?: string;
};

/**
 * Accessible 2D board. Pieces move by click, drag, or keyboard: arrow keys move
 * the focus, Enter or Space selects and moves.
 */
export function Board2D({
  fen,
  orientation = "white",
  movable = "none",
  lastMove,
  arrows,
  tints,
  onMove,
  className,
  label = "Chess board",
}: BoardProps) {
  const { appearance } = useAppearance();
  const material = BOARDS[appearance.board];
  const arrowId = useId();
  const ui = useBoardInteraction(fen, movable, onMove);
  const squares = useMemo(() => squaresFor(orientation), [orientation]);
  const boardRef = useRef<HTMLDivElement>(null);
  const [focus, setFocus] = useState<Square>(orientation === "white" ? "e2" : "e7");
  const [drag, setDrag] = useState<{ from: Square; x: number; y: number; moved: boolean; wasSelected: boolean } | null>(null);

  const squareAt = useCallback(
    (clientX: number, clientY: number): Square | null => {
      const r = boardRef.current?.getBoundingClientRect();
      if (!r) return null;
      const col = Math.floor(((clientX - r.left) / r.width) * 8);
      const row = Math.floor(((clientY - r.top) / r.height) * 8);
      if (col < 0 || col > 7 || row < 0 || row > 7) return null;
      return squares[row * 8 + col];
    },
    [squares],
  );

  const onPointerDown = (e: React.PointerEvent, sq: Square) => {
    if (e.button !== 0) return;
    if (ui.canSelect(sq)) {
      const wasSelected = ui.selected === sq;
      ui.setSelected(sq);
      setDrag({ from: sq, x: e.clientX, y: e.clientY, moved: false, wasSelected });
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      e.preventDefault();
    } else {
      ui.clickSquare(sq);
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag) return;
    const moved = drag.moved || Math.hypot(e.clientX - drag.x, e.clientY - drag.y) > 4;
    setDrag({ ...drag, x: e.clientX, y: e.clientY, moved });
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (!drag) return;
    const to = squareAt(e.clientX, e.clientY);
    const d = drag;
    setDrag(null);
    if (!d.moved) {
      // A plain click: select the piece, or deselect it if it was already selected.
      if (d.wasSelected) ui.setSelected(null);
      return;
    }
    if (to && to !== d.from && ui.targets.has(to)) ui.tryMove(d.from, to);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const idx = squares.indexOf(focus);
    let next = idx;
    if (e.key === "ArrowRight") next = idx % 8 < 7 ? idx + 1 : idx;
    else if (e.key === "ArrowLeft") next = idx % 8 > 0 ? idx - 1 : idx;
    else if (e.key === "ArrowUp") next = idx >= 8 ? idx - 8 : idx;
    else if (e.key === "ArrowDown") next = idx < 56 ? idx + 8 : idx;
    else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      ui.clickSquare(focus);
      return;
    } else if (e.key === "Escape") {
      ui.setSelected(null);
      return;
    } else return;
    e.preventDefault();
    const sq = squares[next];
    setFocus(sq);
    boardRef.current?.querySelector<HTMLElement>(`[data-square="${sq}"]`)?.focus();
  };

  const center = (sq: Square) => {
    const i = squares.indexOf(sq);
    return { x: (i % 8) + 0.5, y: Math.floor(i / 8) + 0.5 };
  };

  const selectedPiece = ui.selected ? ui.chess.get(ui.selected) : null;
  const announce = ui.selected && selectedPiece
    ? `${selectedPiece.color === "w" ? "White" : "Black"} ${PIECE_LABEL[selectedPiece.type]} on ${ui.selected} selected. ${ui.targets.size} legal moves.`
    : "";

  return (
    <div className={`board-flat relative aspect-square w-full select-none ${className ?? ""}`} style={{ containerType: "inline-size", "--sq-light": material.light, "--sq-dark": material.dark, "--board-frame": material.frame } as React.CSSProperties}>
      <div
        ref={boardRef}
        role="grid"
        aria-label={`${label}. ${ui.turn === "w" ? "White" : "Black"} to move.`}
        onKeyDown={onKeyDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => setDrag(null)}
        className="grid h-full w-full touch-none grid-cols-8 grid-rows-8 overflow-hidden rounded-md shadow-md ring-1 ring-black/10"
      >
        {/* Gridcells must sit in rows for screen-reader grid navigation; "contents" keeps the CSS grid. */}
        {Array.from({ length: 8 }, (_, rank) => <div key={rank} role="row" className="contents">{squares.slice(rank * 8, rank * 8 + 8).map((sq, file) => {
          const i = rank * 8 + file;
          const piece = ui.chess.get(sq);
          const light = isLightSquare(sq);
          const target = ui.targets.get(sq);
          const isLast = lastMove && (lastMove.from === sq || lastMove.to === sq);
          const row = Math.floor(i / 8);
          const col = i % 8;
          const dragging = drag?.moved && drag.from === sq;
          return (
            <div
              key={sq}
              role="gridcell"
              data-square={sq}
              tabIndex={sq === focus ? 0 : -1}
              aria-label={`${sq}${piece ? `, ${piece.color === "w" ? "white" : "black"} ${PIECE_LABEL[piece.type]}` : ", empty"}${target ? ", legal move" : ""}`}
              aria-selected={ui.selected === sq}
              onPointerDown={(e) => onPointerDown(e, sq)}
              onFocus={() => setFocus(sq)}
              className="relative grid place-items-center outline-none focus-visible:z-10 focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-sky-500"
              style={{ backgroundColor: light ? "var(--sq-light)" : "var(--sq-dark)", backgroundImage: `url(/materials/${material.texture}.webp)`, backgroundSize: "400% 400%", backgroundPosition: `${(i % 4) * 33.33}% ${Math.floor(i / 8) % 4 * 33.33}%`, backgroundBlendMode: "soft-light" }}
            >
              {isLast && <div className="absolute inset-0" style={{ background: "var(--sq-last)" }} />}
              {tints?.[sq] && <div className="absolute inset-0" style={{ background: tints[sq] }} />}
              {ui.selected === sq && <div className="absolute inset-0" style={{ background: "var(--sq-select)" }} />}
              {ui.checkSquare === sq && (
                <div className="absolute inset-0" style={{ background: "radial-gradient(circle, var(--sq-check) 0%, transparent 70%)" }} />
              )}
              {col === 0 && (
                <span className="absolute left-0.5 top-0 text-[2.2cqw] font-semibold" style={{ color: light ? "var(--sq-dark)" : "var(--sq-light)" }}>
                  {sq[1]}
                </span>
              )}
              {row === 7 && (
                <span className="absolute bottom-0 right-1 text-[2.2cqw] font-semibold" style={{ color: light ? "var(--sq-dark)" : "var(--sq-light)" }}>
                  {sq[0]}
                </span>
              )}
              {piece && (
                <span
                  className={`relative z-10 h-[94%] w-[94%] ${ui.canSelect(sq) ? "cursor-grab" : ""}`}
                  style={{ opacity: dragging ? 0.25 : 1 }}
                >
                  <PieceArt type={piece.type} color={piece.color} finish={appearance.pieces} style={appearance.style}/>
                </span>
              )}
              {target &&
                (target.capture ? (
                  <div className="pointer-events-none absolute inset-[4%] z-20 rounded-full border-[0.9cqw]" style={{ borderColor: "var(--sq-hint)" }} />
                ) : (
                  <div className="pointer-events-none absolute z-20 h-[28%] w-[28%] rounded-full" style={{ background: "var(--sq-hint)" }} />
                ))}
            </div>
          );
        })}</div>)}
      </div>

      {arrows && arrows.length > 0 && (
        <svg viewBox="0 0 8 8" className="pointer-events-none absolute inset-0 z-20 h-full w-full" aria-hidden>
          <defs>
            <marker id={arrowId} viewBox="0 0 10 10" refX="5" refY="5" markerWidth="3" markerHeight="3" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="context-stroke" />
            </marker>
          </defs>
          {arrows.map((a, i) => {
            const s = center(a.from);
            const t = center(a.to);
            const dx = t.x - s.x;
            const dy = t.y - s.y;
            const len = Math.hypot(dx, dy) || 1;
            const ex = t.x - (dx / len) * 0.3;
            const ey = t.y - (dy / len) * 0.3;
            return (
              <line
                key={i}
                x1={s.x}
                y1={s.y}
                x2={ex}
                y2={ey}
                stroke={a.color ?? "rgba(21, 128, 61, 0.8)"}
                strokeWidth={0.16}
                strokeLinecap="round"
                markerEnd={`url(#${arrowId})`}
              />
            );
          })}
        </svg>
      )}

      {drag?.moved && selectedPiece && (
        <span
          className="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-1/2"
          style={{ left: drag.x, top: drag.y, width: "12cqw", height: "12cqw" }}
          aria-hidden
        >
          <PieceArt type={selectedPiece.type} color={selectedPiece.color} finish={appearance.pieces} style={appearance.style}/>
        </span>
      )}

      {ui.pendingPromotion && <PromotionPicker color={ui.turn} onChoose={ui.choosePromotion} />}
      <p className="sr-only" aria-live="polite">
        {announce}
      </p>
      <span className="sr-only">Files {FILES.join(" ")}</span>
    </div>
  );
}
